/**
 * Database Migration Utility
 * 
 * Handles IndexedDB schema migrations and version upgrades.
 * Provides a structured way to define database migrations.
 */

export const DB_NAME = 'KissandraDB';

// Current schema version
export const CURRENT_DB_VERSION = 7;

/**
 * Migration definitions
 * Each key represents a version number, and the value is a function that performs the migration
 * The function receives the database and transaction objects as parameters
 */
export const migrations = {
    // Version 1 - Initial schema
    1: (db, transaction) => {
        console.log('Running migration for version 1');
        
        // Create tag usage store
        if (!db.objectStoreNames.contains('tag_usage')) {
            const tagUsageStore = db.createObjectStore('tag_usage', { keyPath: 'tag' });
            tagUsageStore.createIndex('suggestionType', 'suggestionType', { unique: false });
            tagUsageStore.createIndex('usage', 'usage', { unique: false });
        }
    },
    // Version 6 - Core character stores setup (characters, images, details, etc.)
    6: (db) => {
        console.log('Running migration for version 6: creating core character stores');

        const ensureStore = (name, options = { keyPath: 'id' }, indexes = []) => {
            if (!db.objectStoreNames.contains(name)) {
                const store = db.createObjectStore(name, options);
                indexes.forEach(({ name, keyPath, opts }) => store.createIndex(name, keyPath, opts || { unique: false }));
            }
        };

        // characters store
        ensureStore('characters');

        // character_images store with index on character_id
        ensureStore('character_images', { keyPath: 'id', autoIncrement: true }, [
            { name: 'character_id', keyPath: 'character_id' }
        ]);

        // character_details store composite index (character_field)
        ensureStore('character_details', { keyPath: 'id', autoIncrement: true }, [
            { name: 'character_id', keyPath: 'character_id' },
            { name: 'character_field', keyPath: ['character_id', 'field_name'], opts: { unique: true } }
        ]);



        // user_character_data store with character_id pk
        ensureStore('user_character_data', { keyPath: 'character_id' });

        // metadata store with key pk
        ensureStore('metadata', { keyPath: 'key' });
    },
    // Version 7 - Drop legacy stores; OAuth and account flows only need metadata
    7: (db) => {
        console.log('Running migration for version 7: removing legacy character/tag stores');
        const legacy = ['characters', 'character_images', 'character_details', 'user_character_data', 'tag_usage'];
        for (const name of legacy) {
            if (db.objectStoreNames.contains(name)) {
                db.deleteObjectStore(name);
            }
        }
    }
};

/**
 * Apply migrations for a database upgrade
 * @param {IDBDatabase} db - The database being upgraded
 * @param {IDBTransaction} transaction - The upgrade transaction
 * @param {number} oldVersion - The previous version of the database
 * @param {number} newVersion - The new version of the database
 */
export function applyMigrations(db, transaction, oldVersion, newVersion) {
    console.log(`Upgrading database from version ${oldVersion} to ${newVersion}`);
    
    // Apply each migration in sequence
    for (let version = oldVersion + 1; version <= newVersion; version++) {
        if (migrations[version]) {
            console.log(`Applying migration for version ${version}`);
            migrations[version](db, transaction);
        } else {
            console.warn(`No migration defined for version ${version}`);
        }
    }
}

/**
 * Check if a database upgrade is needed
 * @returns {Promise<boolean>} - Whether an upgrade is needed
 */
export async function needsDatabaseUpgrade() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME);
        
        request.onsuccess = (event) => {
            const db = event.target.result;
            const currentVersion = db.version;
            db.close();
            
            resolve(currentVersion < CURRENT_DB_VERSION);
        };
        
        request.onerror = (event) => {
            reject(new Error(`Failed to check database version: ${event.target.error}`));
        };
    });
}

/**
 * Upgrade the database to the latest version
 * @returns {Promise<void>}
 */
export async function upgradeDatabaseSchema() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, CURRENT_DB_VERSION);
        
        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            const transaction = event.target.transaction;
            const oldVersion = event.oldVersion;
            
            applyMigrations(db, transaction, oldVersion, CURRENT_DB_VERSION);
        };
        
        request.onsuccess = () => {
            console.log(`Database upgraded to version ${CURRENT_DB_VERSION}`);
            resolve();
        };
        
        request.onerror = (event) => {
            reject(new Error(`Failed to upgrade database: ${event.target.error}`));
        };
    });
}

/**
 * Initialize database with versioning
 * @returns {Promise<void>}
 */
export async function initDatabaseWithVersioning() {
    try {
        // Check if upgrade is needed
        const needsUpgrade = await needsDatabaseUpgrade();
        
        if (needsUpgrade) {
            console.log('Database upgrade needed, applying migrations...');
            await upgradeDatabaseSchema();
        } else {
            console.log('Database schema is up to date');
        }
    } catch (error) {
        console.error('Error initializing database with versioning:', error);
        throw error;
    }
} 
